import { DEFAULT_PHOTO_RATIO } from 'services/PhotoBook/PhotoBook.consts'
import type { StoredPhotoBook } from 'services/PhotoBook/photoBookSaver.types'
import {
  isAiGeneratedCover,
  isDesignedCover,
} from 'components/PhotoBook/PhotoBookProvider/PhotoBook.utils'
import type { PhotoBankStore } from 'stores/photoBankStore/PhotoBankStore'
import { getMaxWidthFetchUrl } from 'services/CloudinaryService'
import { isPrivatePhotoUrl } from 'components/PhotoBank/PhotoBank.utils'
import { DEFAULT_THUMB_CROP, type PhotoBookCartItem } from '../../CartItem'

// Cap the AI cover's full-res print image to a small variant for the 80px cart thumb.
const CART_COVER_THUMB_WIDTH = 240

export function buildCover(
  cover: StoredPhotoBook['cover'],
  photoBankStore: PhotoBankStore
): PhotoBookCartItem['metadata']['cover'] {
  if (isDesignedCover(cover)) {
    return {
      type: 'designed',
      designedCoverId: cover.designedCoverId,
    }
  }

  if (isAiGeneratedCover(cover)) {
    return {
      type: 'ai-generated',
      thumb: {
        // A private cover is shown through its preview link, which BookThumbnail builds when it renders
        thumbUrl: isPrivatePhotoUrl(cover.coverUrl)
          ? cover.coverUrl
          : getMaxWidthFetchUrl(cover.coverUrl, CART_COVER_THUMB_WIDTH),
        crop: DEFAULT_THUMB_CROP,
        // The headline is baked into the generated image; no overlaid title.
        coverText: null,
      },
    }
  }

  const coverPhoto = photoBankStore.getPhoto(cover.photo.uid)
  const coverThumbnailUrl = coverPhoto
    ? coverPhoto.minimalResUrl ?? coverPhoto.url
    : null
  // Cover crops are stored relative (0-1) to the photo, so they map directly
  // onto the thumbnail without normalization (same as the 3D cover material).
  const coverCropParams = cover.photo.cropParams[DEFAULT_PHOTO_RATIO]
  const coverCrop =
    coverCropParams?.width && coverCropParams?.height
      ? {
          xRatio: coverCropParams.x ?? 0,
          yRatio: coverCropParams.y ?? 0,
          widthRatio: coverCropParams.width,
          heightRatio: coverCropParams.height,
        }
      : DEFAULT_THUMB_CROP

  return {
    type: 'photo',
    thumb: {
      thumbUrl: coverThumbnailUrl,
      crop: coverCrop,
      coverText: cover.caption.text ?? null,
      coverTextStyle: cover.caption.style,
      coverFontHeight: cover.caption.fontHeight,
    },
  }
}
